import { useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import ClaimVoice from './pages/ClaimVoice';
import LuluVerify from './pages/LuluVerify';
import CaseIntelligence from './pages/CaseIntelligence';
import Dashboard from './pages/Dashboard';
import Pricing from './pages/Pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/veritascore-logo.png" alt="VeritasCore Logo" className="h-12 w-12" />
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">VERITASCORE.IO</h1>
                <p className="text-xs text-gray-400 mt-0.5">Truth. Verified. Scored.</p>
              </div>
            </div>
            <a 
              href="https://veritascore.io" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-teal-400 hover:text-teal-300 transition-colors"
            >
              veritascore.io
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white mb-6">
            Truth. Verified. Scored.
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Turn real-world incidents into verified, scored reports in minutes.
          </p>
          <p className="text-sm text-teal-400 mt-3">Powered by VeritasCore — the intelligence engine behind real-world verification</p>
        </div>

        {/* Three Products */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* ClaimVoice */}
          <div 
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all cursor-pointer group"
            onClick={() => navigate('/claimvoice')}
            data-testid="claimvoice-card"
          >
            <div className="w-16 h-16 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-500/30 transition-all">
              <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">ClaimVoice</h3>
            <p className="text-gray-400 mb-4">Capture what happened. Speak your story and turn it into a structured statement.</p>
            <button className="text-blue-400 font-semibold hover:text-blue-300 transition-colors">
              Start Recording →
            </button>
          </div>

          {/* LuluVerify */}
          <div 
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all cursor-pointer group"
            onClick={() => navigate('/luluverify')}
            data-testid="luluverify-card"
          >
            <div className="w-16 h-16 bg-green-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-green-500/30 transition-all">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">LuluVerify</h3>
            <p className="text-gray-400 mb-4">Prove it's real. Upload photos, capture location, and verify evidence.</p>
            <button className="text-green-400 font-semibold hover:text-green-300 transition-colors">
              Add Evidence →
            </button>
          </div>

          {/* Case Intelligence */}
          <div 
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all cursor-pointer group"
            onClick={() => navigate('/intelligence')}
            data-testid="intelligence-card"
          >
            <div className="w-16 h-16 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-purple-500/30 transition-all">
              <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Case Intelligence</h3>
            <p className="text-gray-400 mb-4">Analyze and structure matters with credibility scoring and evidence organization.</p>
            <button className="text-purple-400 font-semibold hover:text-purple-300 transition-colors">
              View Analysis →
            </button>
          </div>
        </div>

        {/* Dashboard Link */}
        <div className="text-center mb-20">
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all border border-white/20"
            data-testid="view-dashboard-btn"
          >
            View All Claims Dashboard
          </button>
        </div>
      </div>

      {/* FLAGSHIP REPORT SECTION - PROOF */}
      <div className="max-w-4xl mx-auto px-6 py-20 border-t border-white/10">
        <div className="text-center mb-12">
          <h3 className="text-4xl font-bold text-white mb-3">Real Output. Not a Demo.</h3>
          <p className="text-xl text-gray-400">This is what VeritasCore produces.</p>
        </div>

        <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border-2 border-white/20 rounded-3xl p-10">
          
          {/* SCORE BLOCK - THE HOOK */}
          <div className="text-center mb-10 pb-10 border-b border-white/10">
            <div className="inline-block">
              <div className="text-sm text-gray-400 mb-2 tracking-wide">VERITAS SCORE™</div>
              <div className="text-8xl font-bold text-teal-400 mb-2">94</div>
              <div className="text-2xl text-white mb-1">/ 100</div>
              <div className="inline-flex items-center space-x-2 bg-teal-500/20 border border-teal-500/50 rounded-full px-6 py-2 mt-4">
                <svg className="w-5 h-5 text-teal-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-teal-300 font-bold text-lg">Premium — High Credibility</span>
              </div>
            </div>
          </div>

          {/* WHY THIS SCORE - EXPLAINABILITY */}
          <div className="mb-10">
            <h4 className="text-2xl font-bold text-white mb-6 text-center">Why this score:</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-teal-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-lg text-gray-300">Narrative aligns with collision dynamics and witness accounts</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-teal-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-lg text-gray-300">Multi-angle evidence confirms impact sequence</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-teal-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-lg text-gray-300">GPS, timestamps, and supporting data are consistent</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-teal-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-lg text-gray-300">Dual witness corroboration strengthens reliability</p>
              </div>
            </div>
          </div>

          {/* MINI SUMMARY */}
          <div className="bg-white/5 rounded-xl p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Incident:</span>
                <span className="text-white ml-2 font-semibold">Multi-vehicle rear-end collision</span>
              </div>
              <div>
                <span className="text-gray-500">Location:</span>
                <span className="text-white ml-2 font-semibold">Fort Myers, FL</span>
              </div>
              <div>
                <span className="text-gray-500">Evidence:</span>
                <span className="text-white ml-2 font-semibold">16 photos + 2 witness statements</span>
              </div>
              <div>
                <span className="text-gray-500">Estimated Value Range:</span>
                <span className="text-white ml-2 font-semibold">$125,000–$175,000</span>
              </div>
            </div>
          </div>

          {/* KILLER QUOTE */}
          <div className="bg-gradient-to-r from-teal-500/20 to-blue-500/20 border-2 border-teal-500/40 rounded-xl p-6 mb-8">
            <div className="flex items-start space-x-4">
              <svg className="w-8 h-8 text-teal-400 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
              </svg>
              <p className="text-xl text-white font-medium leading-relaxed">
                "VeritasCore™ gave us a complete, verified case file before we even talked to the client."
              </p>
            </div>
          </div>

          {/* CTA BUTTON */}
          <div className="text-center">
            <button
              onClick={() => navigate('/intelligence?claim_id=a92d8dd4-135f-45e0-b719-984be86ee8a0')}
              className="bg-teal-500 hover:bg-teal-600 text-white px-12 py-5 rounded-xl font-bold text-xl transition-all inline-flex items-center space-x-3 shadow-lg shadow-teal-500/50"
              data-testid="view-full-report-btn"
            >
              <span>View Full VeritasCore Report</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
            <p className="text-sm text-gray-500 mt-4">Complete analysis with 5-component breakdown + explainability</p>
          </div>

        </div>
      </div>

      {/* How It Works */}
      <div className="max-w-7xl mx-auto px-6 py-16 border-t border-white/10">
        <h3 className="text-3xl font-bold text-white text-center mb-4">How VeritasCore Works</h3>
        <p className="text-center text-gray-400 mb-12">The intelligence layer that scores the credibility of real-world events</p>
        <div className="grid md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-4xl font-bold text-blue-400 mb-4">1</div>
            <h4 className="text-xl font-semibold text-white mb-2">ClaimVoice</h4>
            <p className="text-gray-400">Captures the story</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-green-400 mb-4">2</div>
            <h4 className="text-xl font-semibold text-white mb-2">LuluVerify</h4>
            <p className="text-gray-400">Captures the proof</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-purple-400 mb-4">3</div>
            <h4 className="text-xl font-semibold text-white mb-2">Case Intelligence</h4>
            <p className="text-gray-400">Analyzes and structures the case</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-teal-400 mb-4">✓</div>
            <h4 className="text-xl font-semibold text-white mb-2">VeritasCore</h4>
            <p className="text-gray-400">Scores credibility</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/claimvoice" element={<ClaimVoice />} />
          <Route path="/luluverify" element={<LuluVerify />} />
          <Route path="/intelligence" element={<CaseIntelligence />} />
          <Route path="/hussarai" element={<CaseIntelligence />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/pricing" element={<Pricing />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;